import { Injectable } from '@angular/core';
import { Study } from '@models';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PortfolioStatsService {
  private activeStudiesCount = new BehaviorSubject<number>(0);
  private startupStudiesCount = new BehaviorSubject<number>(0);
  private enrollmentStudiesCount = new BehaviorSubject<number>(0);
  private maintenanceStudiesCount = new BehaviorSubject<number>(0);
  private closingStudiesCount = new BehaviorSubject<number>(0);
  private subjectsEnrolledCount = new BehaviorSubject<number>(0);
  private countriesCount = new BehaviorSubject<number>(0);

  activeStudiesCount$ = this.activeStudiesCount.asObservable();
  startupStudiesCount$ = this.startupStudiesCount.asObservable();
  enrollmentStudiesCount$ = this.enrollmentStudiesCount.asObservable();
  maintenanceStudiesCount$ = this.maintenanceStudiesCount.asObservable();
  closingStudiesCount$ = this.closingStudiesCount.asObservable();
  subjectsEnrolledCount$ = this.subjectsEnrolledCount.asObservable();
  countriesCount$ = this.countriesCount.asObservable();

  private readonly stages = {
    startup: this.startupStudiesCount,
    enrollment: this.enrollmentStudiesCount,
    maintenance: this.maintenanceStudiesCount,
    closeout: this.closingStudiesCount
  };

  calculatePortfolioStats(studies: Study[]): void {
    const lower = (val?: string) => val?.toLowerCase() || '';

    this.activeStudiesCount.next(
      studies.filter((study) => lower(study.studyStatus).includes('active')).length
    );

    // Count per study stage
    Object.entries(this.stages).forEach(([stage, subject]) => {
      const count = studies.filter((study) => lower(study.studyStage).includes(stage)).length;
      subject.next(count);
    });

    // Unique countries
    const uniqueCountries = new Set<string>();
    studies.forEach((study) => {
      this.parseCountries(study).forEach((c) => uniqueCountries.add(c));
    });
    this.countriesCount.next(uniqueCountries.size);

    // Total subjects enrolled
    const totalEnrolled = studies.reduce(
      (sum, study) => sum + this.parseEnrolledSubjects(study),
      0
    );
    this.subjectsEnrolledCount.next(totalEnrolled);
  }

  private parseCountries(study: Study): string[] {
    if (study.countryList?.length) {
      return study.countryList.map((c) => c.trim());
    }
    if (study.country?.length) {
      return study.country.split(',').map((c) => c.trim());
    }
    return [];
  }

  private parseEnrolledSubjects(study: Study): number {
    const text = study.subjectsEnrolled;
    if (!text) return 0;
    const parts = text.split(' of ');
    if (parts.length === 2) {
      const enrolled = parseInt(parts[0], 10);
      return isNaN(enrolled) ? 0 : enrolled;
    }
    return 0;
  }
}
